import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  serverTimestamp,
  onSnapshot,
  type Unsubscribe,
} from '@/lib/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/stores/auth'
import { useActivityLog } from '@/composables/useActivityLog'
import { useNotificationsStore } from '@/stores/notifications'
import { getTenantConstraint, getTenantData } from '@/utils/tenantQuery'
import type { Project, ProjectStatus, ProjectType } from '@/types'
import type { ProjectsState } from './state'

export interface CreateProjectData {
  name: string
  key: string // Project key for issue prefixes, e.g., "PROJ"
  description?: string
  organizationId: string
  type: ProjectType
  status?: ProjectStatus
  operatorIds?: string[]
  leadId?: string
  startDate?: Date
  endDate?: Date
  estimatedBudget?: number
  technologies?: string[]
  repositoryUrl?: string
  stagingUrl?: string
  productionUrl?: string
  defaultDefinitionOfDone?: string[]
  contextDocumentId?: string // Reference to context document for AI assistant
}

export interface UpdateProjectData {
  name?: string
  key?: string
  description?: string
  type?: ProjectType
  status?: ProjectStatus
  operatorIds?: string[]
  leadId?: string
  startDate?: Date
  endDate?: Date
  estimatedBudget?: number
  technologies?: string[]
  repositoryUrl?: string
  stagingUrl?: string
  productionUrl?: string
  defaultDefinitionOfDone?: string[]
  contextDocumentId?: string
  agentDocumentId?: string
  planningDocumentId?: string
  pinnedDocuments?: import('@/types').PinnedDocument[]
}

export const createActions = (state: ProjectsState) => {
  const { logCreate, logUpdate, logDelete, detectChanges } = useActivityLog()
  const notificationsStore = useNotificationsStore()
  let globalUnsubscribe: Unsubscribe | null = null

  const mapProjectDoc = (docSnap: { id: string; data: () => Record<string, unknown> }): Project => {
    const data = docSnap.data()
    return {
      id: docSnap.id,
      tenantId: data.tenantId,
      name: data.name,
      key: data.key || '',
      description: data.description,
      organizationId: data.organizationId,
      type: data.type,
      status: data.status,
      operatorIds: data.operatorIds || [],
      leadId: data.leadId,
      startDate: (data.startDate as any)?.toDate?.(),
      endDate: (data.endDate as any)?.toDate?.(),
      estimatedBudget: data.estimatedBudget,
      technologies: data.technologies || [],
      repositoryUrl: data.repositoryUrl,
      stagingUrl: data.stagingUrl,
      productionUrl: data.productionUrl,
      epicCounter: data.epicCounter || 0,
      requirementCounter: data.requirementCounter || 0,
      defaultDefinitionOfDone: data.defaultDefinitionOfDone || [],
      contextDocumentId: data.contextDocumentId,
      agentDocumentId: data.agentDocumentId,
      planningDocumentId: data.planningDocumentId,
      pinnedDocuments: data.pinnedDocuments || [],
      createdAt: (data.createdAt as any)?.toDate?.() || new Date(),
      updatedAt: (data.updatedAt as any)?.toDate?.() || new Date(),
      createdBy: data.createdBy,
      updatedBy: data.updatedBy,
    } as Project
  }

  const buildProjectQuery = () => {
    const authStore = useAuthStore()
    const tenantConstraint = getTenantConstraint()
    const baseConstraints = tenantConstraint ? [tenantConstraint] : []

    if (authStore.user?.role === 'client' || authStore.user?.role === 'member') {
      if (authStore.user.organizationId) {
        return query(
          collection(db, 'projects'),
          ...baseConstraints,
          where('organizationId', '==', authStore.user.organizationId),
          orderBy('createdAt', 'desc')
        )
      }
      // No org assigned — return empty query to prevent seeing all projects
      return query(collection(db, 'projects'), where('__name__', '==', '__none__'))
    } else if (authStore.user?.role === 'operator') {
      return query(
        collection(db, 'projects'),
        ...baseConstraints,
        where('operatorIds', 'array-contains', authStore.user.id),
        orderBy('createdAt', 'desc')
      )
    }

    return query(collection(db, 'projects'), ...baseConstraints, orderBy('createdAt', 'desc'))
  }

  const subscribeGlobal = (): void => {
    if (globalUnsubscribe) return
    state.isLoading = true
    const q = buildProjectQuery()
    globalUnsubscribe = onSnapshot(q, (snapshot) => {
      state.projects = snapshot.docs.map(mapProjectDoc)
      state.isLoading = false
    }, (error) => {
      console.error('Projects subscription error:', error)
      state.isLoading = false
    })
  }

  const unsubscribeGlobal = (): void => {
    if (globalUnsubscribe) {
      globalUnsubscribe()
      globalUnsubscribe = null
    }
  }

  const fetchProjects = async (): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const authStore = useAuthStore()
      const tenantConstraint = getTenantConstraint()
      const baseConstraints = tenantConstraint ? [tenantConstraint] : []

      let q = query(collection(db, 'projects'), ...baseConstraints, orderBy('createdAt', 'desc'))

      // Filter by organization for clients
      if (authStore.user?.role === 'client' || authStore.user?.role === 'member') {
        if (authStore.user.organizationId) {
          q = query(
            collection(db, 'projects'),
            ...baseConstraints,
            where('organizationId', '==', authStore.user.organizationId),
            orderBy('createdAt', 'desc')
          )
        }
      } else if (authStore.user?.role === 'operator') {
        q = query(
          collection(db, 'projects'),
          ...baseConstraints,
          where('operatorIds', 'array-contains', authStore.user.id),
          orderBy('createdAt', 'desc')
        )
      }

      const snapshot = await getDocs(q)

      state.projects = snapshot.docs.map((doc) => mapProjectDoc(doc))
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al cargar proyectos'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const fetchProjectById = async (projectId: string): Promise<Project | null> => {
    try {
      const docRef = doc(db, 'projects', projectId)
      const docSnap = await getDoc(docRef)

      if (!docSnap.exists()) {
        return null
      }

      const data = docSnap.data()
      return {
        id: docSnap.id,
        tenantId: data.tenantId,
        name: data.name,
        key: data.key || '',
        description: data.description,
        organizationId: data.organizationId,
        type: data.type,
        status: data.status,
        operatorIds: data.operatorIds || [],
        leadId: data.leadId,
        startDate: data.startDate?.toDate(),
        endDate: data.endDate?.toDate(),
        estimatedBudget: data.estimatedBudget,
        technologies: data.technologies || [],
        repositoryUrl: data.repositoryUrl,
        stagingUrl: data.stagingUrl,
        productionUrl: data.productionUrl,
        epicCounter: data.epicCounter || 0,
        requirementCounter: data.requirementCounter || 0,
        defaultDefinitionOfDone: data.defaultDefinitionOfDone || [],
        contextDocumentId: data.contextDocumentId,
        agentDocumentId: data.agentDocumentId,
        planningDocumentId: data.planningDocumentId,
        pinnedDocuments: data.pinnedDocuments || [],
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        createdBy: data.createdBy,
        updatedBy: data.updatedBy,
      } as Project
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al cargar proyecto'
      throw error
    }
  }

  const createProject = async (data: CreateProjectData): Promise<Project> => {
    state.isLoading = true
    state.error = null

    try {
      const authStore = useAuthStore()
      const currentUserId = authStore.userId || 'system'

      const firestoreData: Record<string, unknown> = {
        ...getTenantData(),
        name: data.name,
        key: data.key.toUpperCase(),
        organizationId: data.organizationId,
        type: data.type,
        status: data.status || 'definition',
        operatorIds: data.operatorIds || [],
        technologies: data.technologies || [],
        epicCounter: 0,
        requirementCounter: 0,
        defaultDefinitionOfDone: data.defaultDefinitionOfDone || [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: currentUserId,
        updatedBy: currentUserId,
      }

      // Only add optional fields if they have values
      if (data.description) firestoreData.description = data.description
      if (data.leadId) firestoreData.leadId = data.leadId
      if (data.startDate) firestoreData.startDate = data.startDate
      if (data.endDate) firestoreData.endDate = data.endDate
      if (data.estimatedBudget) firestoreData.estimatedBudget = data.estimatedBudget
      if (data.repositoryUrl) firestoreData.repositoryUrl = data.repositoryUrl
      if (data.stagingUrl) firestoreData.stagingUrl = data.stagingUrl
      if (data.productionUrl) firestoreData.productionUrl = data.productionUrl
      if (data.contextDocumentId) firestoreData.contextDocumentId = data.contextDocumentId

      const docRef = await addDoc(collection(db, 'projects'), firestoreData)

      const newProject: Project = {
        id: docRef.id,
        name: data.name,
        key: data.key.toUpperCase(),
        description: data.description,
        organizationId: data.organizationId,
        type: data.type,
        status: data.status || 'definition',
        operatorIds: data.operatorIds || [],
        leadId: data.leadId,
        startDate: data.startDate,
        endDate: data.endDate,
        estimatedBudget: data.estimatedBudget,
        technologies: data.technologies || [],
        repositoryUrl: data.repositoryUrl,
        stagingUrl: data.stagingUrl,
        productionUrl: data.productionUrl,
        epicCounter: 0,
        requirementCounter: 0,
        defaultDefinitionOfDone: data.defaultDefinitionOfDone || [],
        contextDocumentId: data.contextDocumentId,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: currentUserId,
        updatedBy: currentUserId,
      }

      state.projects.unshift(newProject)
      await logCreate('project', newProject.id, newProject.name)

      // Notify admins + assigned operators + lead about new project
      const stakeholders = [...(data.operatorIds || []), data.leadId].filter(Boolean) as string[]
      await notificationsStore.notifyAdminsAndStakeholders(
        stakeholders,
        'project_created',
        'Nuevo proyecto creado',
        `Se ha creado el proyecto "${newProject.name}"`,
        'project',
        newProject.id
      )

      return newProject
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al crear proyecto'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const updateProject = async (projectId: string, data: UpdateProjectData): Promise<Project> => {
    state.isLoading = true
    state.error = null

    try {
      const authStore = useAuthStore()
      const currentUserId = authStore.userId || 'system'

      // Get current project data for change detection
      const currentProject = state.projects.find((p) => p.id === projectId)
      if (!currentProject) {
        throw new Error('Proyecto no encontrado')
      }

      const updateData: Record<string, unknown> = {
        updatedAt: serverTimestamp(),
        updatedBy: currentUserId,
      }

      // Only add fields that are being updated
      if (data.name !== undefined) updateData.name = data.name
      if (data.key !== undefined) updateData.key = data.key.toUpperCase()
      if (data.description !== undefined) updateData.description = data.description
      if (data.type !== undefined) updateData.type = data.type
      if (data.status !== undefined) updateData.status = data.status
      if (data.operatorIds !== undefined) updateData.operatorIds = data.operatorIds
      if (data.leadId !== undefined) updateData.leadId = data.leadId
      if (data.startDate !== undefined) updateData.startDate = data.startDate
      if (data.endDate !== undefined) updateData.endDate = data.endDate
      if (data.estimatedBudget !== undefined) updateData.estimatedBudget = data.estimatedBudget
      if (data.technologies !== undefined) updateData.technologies = data.technologies
      if (data.repositoryUrl !== undefined) updateData.repositoryUrl = data.repositoryUrl
      if (data.stagingUrl !== undefined) updateData.stagingUrl = data.stagingUrl
      if (data.productionUrl !== undefined) updateData.productionUrl = data.productionUrl
      if (data.defaultDefinitionOfDone !== undefined) updateData.defaultDefinitionOfDone = data.defaultDefinitionOfDone
      if (data.contextDocumentId !== undefined) updateData.contextDocumentId = data.contextDocumentId
      if (data.agentDocumentId !== undefined) updateData.agentDocumentId = data.agentDocumentId
      if (data.planningDocumentId !== undefined) updateData.planningDocumentId = data.planningDocumentId
      if (data.pinnedDocuments !== undefined) updateData.pinnedDocuments = data.pinnedDocuments

      await updateDoc(doc(db, 'projects', projectId), updateData)

      // Detect changes for activity log
      const changes = detectChanges(
        currentProject as unknown as Record<string, unknown>,
        data as Partial<Record<string, unknown>>,
        ['name', 'description', 'type', 'status', 'operatorIds', 'technologies']
      )

      const updatedProject: Project = {
        ...currentProject,
        ...data,
        updatedAt: new Date(),
        updatedBy: currentUserId,
      }

      // Update in local state
      const index = state.projects.findIndex((p) => p.id === projectId)
      if (index !== -1) {
        state.projects[index] = updatedProject
      }

      if (state.selectedProject?.id === projectId) {
        state.selectedProject = updatedProject
      }

      if (changes.length > 0) {
        await logUpdate('project', projectId, changes, updatedProject.name)
      }

      return updatedProject
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al actualizar proyecto'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const deleteProject = async (projectId: string): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const project = state.projects.find((p) => p.id === projectId)

      await deleteDoc(doc(db, 'projects', projectId))

      state.projects = state.projects.filter((p) => p.id !== projectId)

      if (state.selectedProject?.id === projectId) {
        state.selectedProject = null
      }

      if (project) {
        await logDelete('project', projectId, project.name)
      }
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al eliminar proyecto'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const updateProjectStatus = async (projectId: string, status: ProjectStatus): Promise<void> => {
    await updateProject(projectId, { status })
  }

  const setSelectedProject = (project: Project | null): void => {
    state.selectedProject = project
  }

  const setFilters = (filters: Partial<ProjectsState['filters']>): void => {
    state.filters = { ...state.filters, ...filters }
  }

  const clearError = (): void => {
    state.error = null
  }

  return {
    subscribeGlobal,
    unsubscribeGlobal,
    fetchProjects,
    fetchProjectById,
    createProject,
    updateProject,
    deleteProject,
    updateProjectStatus,
    setSelectedProject,
    setFilters,
    clearError,
  }
}
